import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { QTipDataAttributes } from '../Tooltip/QTip.types';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { SeeqActionDropdownItems, SeeqActionDropdownProps } from './SeeqActionDropdown.types';
import { InsertSeeqContent, SeeqActionDropdownItem, ViewWorkbench } from './variants';

const borderStyles = [
  'tw:border-solid',
  'tw:border',
  'tw:rounded-md',
  'tw:border-sq-disabled-gray',
  'tw:dark:border-gray-500',
].join(' ');

const bgStyles = ['tw:bg-sq-white', 'tw:dark:bg-sq-dark-background'].join(' ');

const disabledClasses = ['tw:opacity-50', 'tw:cursor-not-allowed'].join(' ');

const renderItem = (variant: SeeqActionDropdownProps['variant'], item: SeeqActionDropdownItems) => {
  switch (variant) {
    case 'create-workbench':
      return <SeeqActionDropdownItem {...item} />;
    case 'view-workbench':
      return <ViewWorkbench {...item} />;
    case 'insert-seeq-content':
      return <InsertSeeqContent {...item} />;
    default:
      return <SeeqActionDropdownItem {...item} />;
  }
};

const SeeqActionDropdown: React.FunctionComponent<SeeqActionDropdownProps> = ({
  seeqActionDropdownItems,
  trigger,
  id,
  extraClassNames,
  containerTestId,
  disabled = false,
  align = 'end',
  placement = 'bottom',
  placementOffset = 5,
  alignOffset = -35,
  hasArrow = false,
  onOpenChange,
  isOpen,
  setFocusOnTriggerOnClose = true,
  keepFocusInsideDropdown = true,
  variant,
  ...tooltipProps
}) => {
  const tooltipData: QTipDataAttributes | undefined = getQTipData(tooltipProps);

  return (
    <DropdownMenu.Root defaultOpen={false} open={isOpen} onOpenChange={onOpenChange} modal={keepFocusInsideDropdown}>
      <DropdownMenu.Trigger
        id={id}
        data-testid={id}
        className={`tw:border-none tw:focus-visible:outline-none tw:focus:outline-none tw:focus-within:outline-none`}
        asChild
        disabled={disabled}>
        <div
          {...tooltipData}
          className={`tw:bg-transparent tw:flex tw:flex-col tw:items-center tw:focus-visible:outline-none tw:focus:outline-none tw:focus-within:outline-none ${
            disabled ? disabledClasses : ''
          } ${extraClassNames || ''}`}>
          {trigger}
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        sideOffset={placementOffset}
        side={placement}
        align={align}
        alignOffset={alignOffset}
        asChild
        onCloseAutoFocus={(e) => !setFocusOnTriggerOnClose && e.preventDefault()}
        className="tw:focus-visible:outline-none tw:outline-none">
        <div
          data-testid={containerTestId}
          className={
            bgStyles +
            ' tw:relative tw:z-[1200] tw:min-w-6 tw:py-2 tw:shadow-[0_4px_8px_rgba(0,0,0,0.2)]' +
            ' tw:focus-visible:outline-none tw:outline-none tw:data-[state=open]:animate-in' +
            ' tw:data-[state=closed]:animate-out forceFont tw:data-[side=top]:animate-slideDownAndFade' +
            ' tw:data-[side=right]:animate-slideLeftAndFade tw:data-[side=bottom]:animate-slideUpAndFade' +
            ' tw:data-[side=left]:animate-slideRightAndFade ' +
            borderStyles
          }>
          {hasArrow && (
            <DropdownMenu.Arrow asChild>
              <div className=" tw:fill-transparent tw:bg-sq-white tw:w-sq-15 tw:h-sq-15 tw:rotate-45 tw:dark:bg-sq-dark-background tw:border-b tw:border-r tw:border-sq-disabled-gray tw:dark:border-gray-500 tw:-mt-sq-7" />
            </DropdownMenu.Arrow>
          )}
          {seeqActionDropdownItems.map((item, index) => {
            return (
              <div key={item.id ?? index}>
                <DropdownMenu.Item
                  onSelect={(e) => {
                    item.action(e);
                  }}
                  className={`tw:cursor-pointer tw:flex tw:dark:text-sq-white tw:hover:bg-sq-gray-highlight tw:dark:hover:bg-sq-gray-highlight-dark tw:relative tw:select-none tw:outline-none tw:data-[disabled]:text-sq-disabled-gray tw:data-[dim]:opacity-50 tw:aria-disabled:pointer-events-none`}
                  data-testid={item.testId}
                  data-dim={item.dim ? '' : undefined}
                  disabled={!item.enabled}>
                  {renderItem(variant, item)}
                </DropdownMenu.Item>
                {item.divider && (
                  <DropdownMenu.Separator
                    data-testid={`dropdown-divider-${index}`}
                    className="tw:h-px tw:bg-sq-disabled-gray tw:dark:bg-gray-500 tw:my-2"
                  />
                )}
              </div>
            );
          })}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export default SeeqActionDropdown;
